import { PROJECT_STATUS_LABELS } from '@company/shared'
import { Link } from '@tanstack/react-router'
import { Plus, FolderKanban } from 'lucide-react'
import { useState } from 'react'

import {
  useTeamProjects,
  useCreateTeamProject,
  type CreateTeamProjectInput          } from '@/hooks'
import { cn } from '@/lib/utils'
import type {
 ProjectStatus } from '@/hooks';


interface TeamProjectsProps {
  teamId: string
  isArchived?: boolean
}

const STATUS_COLORS: Record<ProjectStatus, string> = {
  planning: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  archived: 'bg-yellow-100 text-yellow-800',
}

/**
 * 团队项目列表组件
 */
export function TeamProjects({ teamId, isArchived }: TeamProjectsProps) {
  const [showCreateForm, setShowCreateForm] = useState(false)

  const { data: projects, isLoading, error } = useTeamProjects(teamId)

  if (isLoading) {
    return <div className="text-muted-foreground">加载中...</div>
  }

  if (error) {
    return <div className="text-destructive">加载失败: {error.message}</div>
  }

  return (
    <div className="space-y-4">
      {/* 新建项目按钮 */}
      {!isArchived && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            新建项目
          </button>
        </div>
      )}

      {/* 项目列表 */}
      {!projects || projects.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <FolderKanban className="h-10 w-10 text-muted-foreground" />
          <div className="text-center">
            <p className="font-medium">暂无项目</p>
            <p className="text-sm text-muted-foreground">
              {isArchived ? '此团队已归档' : '点击上方按钮创建第一个项目'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Link
              key={project.id}
              to="/pm/projects/$projectId"
              params={{ projectId: project.id }}
              className="group rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">{project.code}</p>
                  <h3 className="mt-1 truncate font-semibold group-hover:text-primary">
                    {project.name}
                  </h3>
                </div>
                <span
                  className={cn(
                    'ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                    STATUS_COLORS[project.status]
                  )}
                >
                  {PROJECT_STATUS_LABELS[project.status]}
                </span>
              </div>
              {project.description && (
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                  {project.description}
                </p>
              )}
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                {project.startDate && (
                  <span>开始: {new Date(project.startDate).toLocaleDateString('zh-CN')}</span>
                )}
                {project.endDate && (
                  <span>截止: {new Date(project.endDate).toLocaleDateString('zh-CN')}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* 新建项目表单 */}
      {showCreateForm && (
        <CreateProjectForm
          teamId={teamId}
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => setShowCreateForm(false)}
        />
      )}
    </div>
  )
}

interface CreateProjectFormProps {
  teamId: string
  onClose: () => void
  onSuccess: () => void
}

function CreateProjectForm({ teamId, onClose, onSuccess }: CreateProjectFormProps) {
  const createMutation = useCreateTeamProject()
  const [formData, setFormData] = useState<CreateTeamProjectInput>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = '请输入项目名称'
    } else if (formData.name.length > 100) {
      newErrors.name = '项目名称不能超过100个字符'
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = '截止日期不能早于开始日期'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {return}

    const data: CreateTeamProjectInput = {
      name: formData.name,
      description: formData.description || undefined,
      startDate: formData.startDate ? `${formData.startDate}T00:00:00Z` : undefined,
      endDate: formData.endDate ? `${formData.endDate}T23:59:59Z` : undefined,
    }

    createMutation.mutate(
      { teamId, data },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
      }
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">新建项目</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* 项目名称 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                项目名称 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入项目名称"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* 项目描述 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">项目描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入项目描述"
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>

            {/* 日期 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">开始日期</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">截止日期</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className={cn(
                    'w-full rounded-md border bg-background px-3 py-2 text-sm',
                    errors.endDate && 'border-destructive'
                  )}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-destructive">{errors.endDate}</p>
                )}
              </div>
            </div>

            {/* 错误提示 */}
            {createMutation.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {createMutation.error.message}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {createMutation.isPending ? '创建中...' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
